import { useTranslation } from 'react-i18next';
import styles from './KitPartsCards.module.scss';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { getPartName } from '@app/utils/commonUtils';
import { replaceTokensInString, replaceTokensWithComponents } from '@app/utils';
import ProductLink from '@app/components/common/product/ProductLink';
import { INITIAL_PAGINATION, PAGE_SIZE } from '../constants';
import { useMemo, useRef, useState } from 'react';
import { AlloyButton, Popup } from 'cat-ecommerce-alloy';
import ContactDealerPopup from '@app/components/common/ContactDealerPopup';
import { CatGrid } from '@blocks-react/components/Grid';
import { CatGridItem } from '@blocks-react/components/GridItem';
import { CatCard } from '@blocks-react/components/Card';
import { CatButton } from '@blocks-react/components/Button';
import { CatHeading } from '@blocks-react/components/Heading';
import KitPartDrawerContainer from './KitPartDrawerContainer/KitPartDrawerContainer';
import KitPartImage from './KitPartImage/KitPartImage';
import { useBreakpoint } from '@app/hooks';
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  PAGE_BREAKPOINTS,
  STATUS
} from '@app/constants/commonConstants';
import WrapperContainer from '@app/components/common/WrapperContainer';
import { Drawer, useDrawer } from '@cat-ecom/pcc-components';
import KitPartsCardsTotal from './KitPartsCardsTotal/KitPartsCardsTotal';
import cx from 'classnames';

const AMOUNT_OF_COLUMNS_BLG = 3;
const AMOUNT_OF_COLUMNS_BSM = 2;

const KitPartsCards = ({
  items,
  isPackageWithItemAssociated,
  priceCallStatus,
  quantity,
  isPackage,
  initialPriceCallMade
}) => {
  const hasMoreItemsThanPageSize = items?.length > PAGE_SIZE;
  const [kitItems, setKitItems] = useState(
    hasMoreItemsThanPageSize ? items?.slice(0, PAGE_SIZE) : items
  );
  const [t] = useTranslation();
  const current = useSelector(s => s.products?.current);
  const beanType = useSelector(s => s.products.byId?.[current]?.type);
  const partNumber = useSelector(s => s.products?.byId?.[current]?.partNumber);
  const canLoadMoreItems = kitItems?.length !== items?.length;
  const kitItemsPaginationRef = useRef(INITIAL_PAGINATION);
  const [currentPart, setCurrentPart] = useState({});
  const isBLG = useBreakpoint(PAGE_BREAKPOINTS.BLOCKS_LG);
  const isLoading = priceCallStatus === STATUS.PENDING;
  const enableAddToCart = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.enableAddToCart
  );
  const showTotalKitPrice = enableAddToCart && !isLoading;

  const { drawerProps, openDrawer, isDrawerOpen } = useDrawer({
    hasBackdrop: true
  });

  const kitPartsDrawerProps = {
    ...drawerProps,
    'data-testid': 'part-details-drawer'
  };

  const amountOfColumns = useMemo(
    () => (isBLG ? AMOUNT_OF_COLUMNS_BLG : AMOUNT_OF_COLUMNS_BSM),
    [isBLG]
  );

  const handleShowMoreClick = () => {
    kitItemsPaginationRef.current++;
    setKitItems(prevItems => {
      return [
        ...prevItems,
        ...items.slice(
          prevItems.length,
          PAGE_SIZE * kitItemsPaginationRef.current
        )
      ];
    });
  };
  const popperOptions = {
    placement: 'bottom',
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['top', 'bottom'],
          rootBoundary: 'document'
        }
      }
    ]
  };

  const getDealerInitiator = () => (
    <AlloyButton
      buttonType="link"
      className=" p-0 cat-u-theme-typography-body align-baseline"
      data-testid="contact-dealer-btn"
    >
      {t('PDP_KITS_CONTACT_YOUR_DEALER_LINK')}
    </AlloyButton>
  );
  const contactDealerMsg = replaceTokensWithComponents(
    t('PDP_KITS_NO_ITEMS_ASSOCIATED_CONTACTDEALER'),
    <Popup
      contents={<ContactDealerPopup partNumber={partNumber} />}
      initiator={getDealerInitiator()}
      className={`${styles['availability-contact-dealer__popup']}`}
      popperOptions={popperOptions}
    />
  );
  const kitItemsPageResultsMsg = replaceTokensInString(
    t('MO_Page_Results'),
    1,
    kitItems?.length,
    items?.length
  );
  const noItemsInListMessage = (
    <div className=" u-fs-md--16">
      <CatHeading
        variant="body"
        className="mb-1 d-inline d-sm-inline d-md-block"
      >
        {`${t('PDP_KITS_NO_ITEMS_ASSOCIATED_REASON3')}. `}
      </CatHeading>

      <CatHeading variant="body" className="d-inline">
        {contactDealerMsg}
      </CatHeading>
    </div>
  );

  const displayDrawer = part => {
    setCurrentPart(part);
    openDrawer();
  };

  const partDetailDrawer = (
    <WrapperContainer>
      <Drawer {...kitPartsDrawerProps}>
        <KitPartDrawerContainer part={currentPart} />
      </Drawer>
    </WrapperContainer>
  );

  const isCardOnRightColumn = position => position % amountOfColumns === 0;

  return (
    <div className="kit-parts-cards">
      <div className="pb-3">
        {beanType === 'PackageBean' ? (
          <CatHeading level="h4">{t('KITS_PARTS_IN_KIT')}</CatHeading>
        ) : (
          <CatHeading level="h4">{t('ITEMS_INCLUDED')}</CatHeading>
        )}
      </div>
      {kitItems && (
        <>
          <CatGrid variant="3up" gap="xs" data-testid="kit-items-grid">
            {kitItems?.map((part, index) => {
              const partName = getPartName(part.partNumber, part.name);
              return (
                <CatGridItem key={part.partNumber}>
                  <CatCard removepadding="" data-testid="kit-items-card">
                    <div
                      className={`d-flex align-items-center justify-content-center cat-u-padding-md
                  ${styles['kit-parts-cards__content']}`}
                    >
                      <KitPartImage
                        item={{
                          imageURL: part.thumbnail,
                          name: partName
                        }}
                        isRightColumn={isCardOnRightColumn(index + 1)}
                        className={styles['kit-parts-card__image']}
                      />

                      <div className="d-flex flex-column flex-grow-1 ">
                        <CatButton
                          size={BUTTON_SIZES.MD}
                          variant={BUTTON_VARIANTS.GHOST}
                        >
                          <ProductLink
                            className={cx(
                              'text-start --cat-theme-color-background-utility-information-strong',
                              styles['kit-parts-cards__text']
                            )}
                            handleProductClick={() => displayDrawer(part)}
                            link="#"
                          >
                            {partName}
                          </ProductLink>
                        </CatButton>

                        <CatHeading variant="body-sm" className="ps-3">
                          {t('QUANTITY1')}: {part.quantity}
                        </CatHeading>
                      </div>
                    </div>
                  </CatCard>
                </CatGridItem>
              );
            })}
          </CatGrid>
          <div className="d-flex flex-column d-sm-flex flex-sm-row justify-content-center align-items-center mt-3">
            <CatHeading
              variant="body-sm"
              className="justify-self-center me-sm-3 mb-3 mb-sm-0"
            >
              {kitItemsPageResultsMsg}
            </CatHeading>
            {canLoadMoreItems && (
              <CatButton onClick={handleShowMoreClick}>
                {t('LOAD_MORE')}
              </CatButton>
            )}
          </div>
        </>
      )}
      {showTotalKitPrice && (
        <KitPartsCardsTotal
          quantity={quantity}
          initialPriceCallMade={initialPriceCallMade}
          partNumber={partNumber}
          isPackageWithItemAssociated={isPackageWithItemAssociated}
          isPackage={isPackage}
          priceCallStatus={priceCallStatus}
        />
      )}
      {!isPackageWithItemAssociated && noItemsInListMessage}
      {isDrawerOpen && partDetailDrawer}
    </div>
  );
};

KitPartsCards.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      partNumber: PropTypes.string,
      productURL: PropTypes.string,
      quantity: PropTypes.number,
      shortDescription: PropTypes.string,
      longDescription: PropTypes.string,
      thumbnail: PropTypes.string
    })
  ),
  isPackageWithItemAssociated: PropTypes.bool,
  quantity: PropTypes.string,
  priceCallStatus: PropTypes.string,
  isPackage: PropTypes.bool,
  initialPriceCallMade: PropTypes.bool
};

export default KitPartsCards;
